import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Avatar from '@rentspree/component-2023.components.atoms.avatar'
import Button from '@rentspree/component-2023.components.atoms.button'
import Typography from '@rentspree/component-2023.components.atoms.typography'
import Card from '@rentspree/component-2023.components.organisms.card'
import { useState, useCallback } from 'react'

import { SCREENING_REQUEST_STATUS } from 'constant'
import { useSelector } from 'store'
import { selectPublicListingScreeningRequest } from 'store/public-listing'
import tracker from 'tracker'
import { LOCATION, MAIN_FEATURE } from 'tracker/events'

import { useAuth } from '../../../auth'
import { ContactDrawer } from '../contact-drawer'
import { RequestTourDrawer } from '../request-tour'

import styles from './index.module.scss'

interface WidgetProps {
  contactDetails: ContactDetails
  isDisabledContact?: boolean
  isDisabledApply?: boolean
  isDisabledRequestTour?: boolean
  isPreview?: boolean
}

const Widget: React.FC<WidgetProps> = ({
  contactDetails,
  isDisabledContact,
  isDisabledApply,
  isDisabledRequestTour,
  isPreview,
}) => {
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const { url: screeningRequestApplyLink, status: screeningRequestStatus } = useSelector(
    selectPublicListingScreeningRequest,
  )
  const isApplyButtonVisible =
    !!screeningRequestApplyLink && screeningRequestStatus === SCREENING_REQUEST_STATUS.ENABLED

  const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false)
  const [isOpenRequestTour, setIsOpenRequestTour] = useState(false)

  const { isAuthenticated, redirectToLogin } = useAuth()

  const requestTourButtonLabel = isTablet ? 'Tour' : 'Request a tour'
  const agentName = contactDetails.displayName
  const isTemporaryAgentUser = contactDetails.isTemporary || false

  let caption = '-'
  if (contactDetails.brokerageName && contactDetails.businessName) {
    caption = `${contactDetails.brokerageName} | ${contactDetails.businessName}`
  } else if (contactDetails.businessName) {
    caption = contactDetails.businessName
  } else if (contactDetails.brokerageName) {
    caption = contactDetails.brokerageName
  }

  const handleApply = () => {
    tracker?.trackButtonClick({
      click_text: 'Apply',
      location: LOCATION.PUBLIC_LISTING,
      screen_experience: isTablet ? 'mobile' : 'desktop',
      main_feature: MAIN_FEATURE.MARKET_LISTING,
    })
    const applyURIWithSource = screeningRequestApplyLink?.includes('?')
      ? screeningRequestApplyLink?.concat('&source=public_listing')
      : screeningRequestApplyLink?.concat('?source=public_listing')

    window.open(applyURIWithSource as string, '_blank')
  }

  const handleContactClick = () => {
    tracker?.trackButtonClick({
      click_text: 'Contact Host',
      location: LOCATION.PUBLIC_LISTING,
      screen_experience: isTablet ? 'mobile' : 'desktop',
      main_feature: MAIN_FEATURE.MARKET_LISTING,
    })
    setIsContactDrawerOpen(true)
  }

  const handleRequestTour = () => {
    tracker?.trackButtonClick({
      click_text: 'Tour',
      location: LOCATION.PUBLIC_LISTING,
      screen_experience: isTablet ? 'mobile' : 'desktop',
      main_feature: MAIN_FEATURE.MARKET_LISTING,
    })
    setIsOpenRequestTour(true)
  }

  const handleLogin = () => {
    redirectToLogin()
  }

  return (
    <>
      <ContactDrawer
        onModalClose={useCallback(() => setIsContactDrawerOpen(false), [])}
        isOpen={isContactDrawerOpen}
        contactDetails={contactDetails}
      />
      <RequestTourDrawer
        isOpen={isOpenRequestTour}
        onClose={useCallback(() => setIsOpenRequestTour(false), [])}
        contactDetails={contactDetails}
      />
      <Box className="listing-widget-container">
        <Box className="listing-widget-content">
          <Card className={styles.cardContactContainer}>
            <Box className={styles.cardContactHeader}>
              <Avatar
                color="gray"
                size="large"
                src={contactDetails.imageSrc}
                className={styles.cardContactHeaderAvatar}
              />
              <Box className={styles.cardContactHeaderContent}>
                <Typography variant="body-large">{agentName}</Typography>
                <Typography variant="caption">{caption}</Typography>
                {!isTemporaryAgentUser && !isAuthenticated && (
                  <Button
                    variant="text"
                    size="small"
                    className="underline"
                    color="secondary"
                    wrapped
                    onClick={handleLogin}
                    disabled={isDisabledContact}
                  >
                    Login to contact
                  </Button>
                )}
                {!isTemporaryAgentUser && isAuthenticated && (
                  <Button
                    variant="text"
                    size="small"
                    className="underline"
                    color="secondary"
                    wrapped
                    onClick={handleContactClick}
                    disabled={isDisabledContact}
                  >
                    Contact {contactDetails.firstName}
                  </Button>
                )}
              </Box>
            </Box>
          </Card>
          {(!isTemporaryAgentUser || isApplyButtonVisible) && (
            <Card
              className={`${styles.cardApplyContainer} ${isPreview ? styles.isHideOnMobile : ''}`}
            >
              {!isTablet && (
                <Typography variant="title" lineHeight="48px">
                  Get one step closer to home.
                </Typography>
              )}
              <Box className={styles.cardApplyButtonContainer}>
                {isApplyButtonVisible && (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleApply}
                    disabled={isDisabledApply}
                  >
                    Apply
                  </Button>
                )}
                {!isTemporaryAgentUser && !isAuthenticated && (
                  <Button
                    variant={isTablet ? 'text' : 'outlined'}
                    color="secondary"
                    className="underline"
                    onClick={handleLogin}
                    disabled={isDisabledRequestTour}
                  >
                    Login to {requestTourButtonLabel}
                  </Button>
                )}
                {!isTemporaryAgentUser && isAuthenticated && (
                  <Button
                    variant={isTablet ? 'text' : 'outlined'}
                    color="secondary"
                    className="underline"
                    onClick={handleRequestTour}
                    disabled={isDisabledRequestTour}
                  >
                    {requestTourButtonLabel}
                  </Button>
                )}
              </Box>
            </Card>
          )}
        </Box>
      </Box>
    </>
  )
}
export default Widget
